import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import Layout from '../layout/Layout';

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

export function LegalPageLayout({ title, lastUpdated, children }: LegalPageLayoutProps) {
  const { t } = useTranslation('legal');

  return (
    <Layout className="min-h-screen bg-white flex flex-col" hideHeader={true} fullPage>
      <section className="relative w-full overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{
            background: `
              radial-gradient(circle at top calc(50% + 10px) right calc(0% - 120px), var(--color-gradient-pink) 0%, transparent 25%),
              radial-gradient(circle at bottom calc(0% - 100px) right calc(0% - 150px), var(--color-gradient-purple) 0%, transparent 25%),
              linear-gradient(105deg, var(--color-light) 0%, var(--color-light-lin) 60%, var(--color-gradient-light) 100%)
            `,
          }}
        />

        <nav className="flex justify-between items-center relative z-10 px-4 sm:px-6 lg:px-12 h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/logo/dark-logo.png" alt="Invirtus" className="w-8 h-8 lg:w-10 lg:h-10" />
            <span className="text-dark-invirtus text-3xl lg:text-4xl font-bold">Invirtus</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-dark-invirtus/60 text-sm font-medium hover:text-dark-invirtus transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            {t('shared.back_to_home')}
          </Link>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
          className="relative z-10 px-4 sm:px-6 lg:px-24 pt-8 sm:pt-12 pb-12 sm:pb-16 lg:pb-20"
        >
          <h1 className="text-dark-invirtus text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
            {title}
          </h1>
          <p className="text-dark-invirtus/50 text-sm sm:text-base mt-3">{lastUpdated}</p>
        </motion.div>
      </section>

      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25, ease: 'easeOut' }}
        className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-24 py-10 sm:py-14"
      >
        {children}
      </motion.article>
    </Layout>
  );
}

export function LegalSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-dark-invirtus text-lg sm:text-xl font-semibold mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

export function LegalParagraph({ children }: { children: React.ReactNode }) {
  return <p className="text-dark-invirtus/75 text-[15px] sm:text-base leading-relaxed mb-3 last:mb-0">{children}</p>;
}

export function LegalIntro({ children }: { children: React.ReactNode }) {
  return <p className="text-dark-invirtus/75 text-[15px] sm:text-base leading-relaxed mb-10">{children}</p>;
}

export function LegalSections({ children }: { children: React.ReactNode }) {
  return <div className="space-y-10 text-dark-invirtus/75 text-[15px] sm:text-base leading-relaxed">{children}</div>;
}

export function LegalEmailLink({ email }: { email: string }) {
  return (
    <a href={`mailto:${email}`} className="text-purple font-medium hover:underline transition-colors">
      {email}
    </a>
  );
}
